import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { formatApiError } from "../lib/api";
import {
  ArrowRight, Eye, EyeOff, Mail, Lock, HelpCircle, ShieldCheck,
} from "lucide-react";
import { PLATFORM_COMPANY_NAME } from "../lib/platformCompany";

// Correção final da tela de login — SÓ este arquivo muda neste pacote.
// Nenhum componente interno (Layout, AdminLayout, sidebar, header) é
// reaproveitado ou tocado aqui: a página continua 100% autocontida, então
// nada dentro do sistema pode ser afetado por este redesenho. Proporção
// ~64/36 (painel institucional dominante) e notebook grande inclinado
// seguem a imagem de referência estruturalmente, não como inspiração
// solta. Login social (Google/Microsoft) continua ausente — nunca existiu
// integração real por trás desses botões (removidos no pacote anterior).
export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await login(email, password);
      nav("/");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* LEFT — painel institucional (ASSET ESTÁTICO recortado da arte de
          referência; nada é recriado em CSS: nem notebook, nem benefícios,
          nem composição). Ocupa ~64% no desktop e é ocultado no mobile
          para exibir somente o formulário. */}
      <div className="hidden md:block md:w-[62%] xl:w-[64%] relative overflow-hidden bg-[#050A24]">
        <img
          src="/images/login-hero.webp"
          alt="LOOTUS — Do atendimento ao resultado"
          className="absolute inset-0 w-full h-full object-cover object-left select-none"
          draggable="false"
        />
      </div>

      {/* RIGHT — formulário de acesso (~36% em desktop) */}
      <div className="relative flex-1 flex flex-col bg-white overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-blue-50 opacity-70 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-40 -left-24 w-80 h-80 rounded-full bg-slate-50 opacity-80 blur-3xl pointer-events-none hidden md:block" aria-hidden="true" />

        <div className="relative flex justify-end p-6">
          <a href="mailto:contato@codexpy.com.br" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700">
            <HelpCircle size={14} /> Precisa de ajuda?
          </a>
        </div>

        <div className="relative flex-1 flex items-center justify-center px-6 pb-10">
          <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center font-heading font-black text-2xl text-white" style={{ background: "linear-gradient(135deg, #3B82F6, #1D4ED8)" }}>L</div>
              <div className="mt-3 font-heading font-black text-lg tracking-tight text-slate-900">LOOTUS</div>
              <div className="text-[10px] tracking-[0.25em] text-slate-400">GESTÃO DE SERVIÇOS</div>
            </div>

            <div className="text-center">
              <h2 className="font-heading text-2xl font-bold text-slate-900">Bem-vindo de volta!</h2>
              <p className="text-sm text-slate-500 mt-1">Acesse sua conta para continuar</p>
            </div>

            <div className="mt-8 space-y-4">
              <div>
                <label htmlFor="login-email" className="text-sm font-medium text-slate-700 mb-1 block">E-mail ou usuário</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    id="login-email" data-testid="login-email" type="email" autoComplete="username"
                    value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="ex.: voce@empresa.com.br" required className="h-11 rounded-xl pl-10"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="login-password" className="text-sm font-medium text-slate-700 mb-1 block">Senha</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <Input
                    id="login-password" type={show ? "text" : "password"} autoComplete="current-password"
                    data-testid="login-password" value={password} onChange={e => setPassword(e.target.value)}
                    placeholder="Digite sua senha" required className="h-11 rounded-xl pl-10 pr-10"
                  />
                  <button
                    type="button" onClick={() => setShow(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={show ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {show ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="inline-flex items-center gap-2 cursor-pointer text-slate-600">
                  <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="rounded border-slate-300" />
                  Lembrar meu acesso
                </label>
                <Link to="/forgot-password" className="text-blue-600 hover:underline font-medium" data-testid="link-forgot">Esqueci minha senha</Link>
              </div>
              {err && <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
              <Button
                type="submit" data-testid="login-submit" disabled={busy}
                className="w-full h-12 rounded-xl text-base font-semibold"
                style={{ background: "linear-gradient(90deg, #1D4ED8, #3B82F6)" }}
              >
                <ArrowRight size={18} /> {busy ? "Entrando..." : "Entrar"}
              </Button>
            </div>

            <div className="mt-10 pt-4 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-500">
              <ShieldCheck size={12} /> Desenvolvido por <span className="text-blue-600 font-semibold">{PLATFORM_COMPANY_NAME}</span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
